import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { authFetch } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';
import { useSelected } from '../contexts/SelectedContext';
import { THEME_COLORS, getBudgetContextualStyle } from '../components/Constants';
import SituationCell from '../components/SituationCell';
import AnnualBudgetCell from '../components/AnnualBudgetCell';
import MonthlyBudgetCell from '../components/MonthlyBudgetCell';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';

const styles = {
    container: { padding: '20px' }, 
    headerActions: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    modeToggle: { display: 'flex', borderRadius: '8px', overflow: 'hidden', border: '1px solid #ccc' },
    toggleBtn: { padding: '8px 16px', border: 'none', cursor: 'pointer', transition: '0.3s' },
    topGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' },
    card: { padding: '20px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', backgroundColor: '#fff' },
    dataLine: { display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #eee', alignItems: 'center' },
    input: { width: '130px', textAlign: 'right', padding: '6px', borderRadius: '4px', border: '1px solid #ccc', appearance: 'none', MozAppearance: 'textfield' },
    chartContainer: { height: '40vh', marginTop: '0px', padding: '20px', borderRadius: '12px', backgroundColor: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
    table: { 
        width: '100%', 
        borderCollapse: 'collapse', 
        fontSize: '0.75em', // Légère réduction pour l'adaptation écran
        backgroundColor: '#fff',
        tableLayout: 'fixed' // Aide à forcer le respect de la largeur 100%
    },
    th: { 
        padding: '8px 4px', 
        border: '1px solid #ccc', 
        backgroundColor: '#f0f0f0', 
        position: 'sticky', 
        top: 0, 
        zIndex: 10,
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    },
    categoryCell: { 
        padding: '8px', 
        border: '1px solid #ccc', 
        fontWeight: 'bold', 
        backgroundColor: '#f9f9f9', 
        width: '120px', // Largeur fixe réduite pour laisser de la place aux mois
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    summaryRow: { fontWeight: 'bold' },
    summaryLabel: { padding: '10px', backgroundColor: '#eee', border: '1px solid #ccc' },
    summaryValue: { textAlign: 'center', padding: '10px', border: '1px solid #ccc' },
    futureMonth: { backgroundColor: '#f2f2f2', color: '#999' }
};

// --- FONCTIONS DE FORMATAGE ---

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
        style: 'currency', currency: 'EUR', minimumFractionDigits: 2
    }).format(amount);
};

// Convertit un nombre en chaîne FR pour l'input (ex: 1250.5 -> "1250,50")
const formatInputVal = (val) => {
    return val.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Nettoie la saisie utilisateur (ex: "1 250,50" -> 1250.5)
const parseLocaleNumber = (stringNumber) => {
    if (!stringNumber) return 0;
    const normalized = stringNumber.toString().replace(',', '.').replace(/\s/g, '');
    return parseFloat(normalized) || 0;
};

const MONTHS = ["Janv", "Févr", "Mars", "Avril", "Mai", "Juin", "Juil", "Août", "Sept", "Oct", "Nov", "Déc"];

export default function SituationPage({ selectedYear }) {
    const { currentTheme } = useTheme();
    const { selectedPerson } = useSelected();
    const themeColors = THEME_COLORS[currentTheme] || THEME_COLORS.blue;
    const [rawRows, setRawRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const now = new Date();
    const currentMonthIndex = now.getMonth(); 
    const currentYear = now.getFullYear();

    // --- ÉTATS ---
    const [mode, setMode] = useState(null); 
    const [simuData, setSimuData] = useState({ 
        epargne: 350, 
        salaire: 0, 
        depenses: 0, 
        viewType: 'annual' 
    });
    // États temporaires pour la saisie (évite les blocages pendant que l'utilisateur tape)
    const [tempInputs, setTempInputs] = useState({ salaire: '', depenses: '', epargne: '' });

    // --- PERSISTANCE ---

    const saveSettings = useCallback(async (updates) => {
        if (!selectedPerson?.id) return;
        const payload = {
            person_id: selectedPerson.id,
            year: selectedYear,
            preferred_mode: updates.mode || mode,
            simu_epargne_souhaitee: updates.epargne ?? simuData.epargne,
            simu_salaire_annuel: updates.salaire ?? simuData.salaire,
            simu_depenses_annuelles: updates.depenses ?? simuData.depenses,
            simu_nb_mois: updates.mois ?? simuData.mois,
            simu_view_type: updates.viewType ?? simuData.viewType
        };
        try {
            await authFetch.post('/api/budget-settings', payload);
        } catch (err) { console.error("Erreur sauvegarde", err); }
    }, [selectedPerson?.id, selectedYear, mode, simuData]);

    const fetchData = useCallback(async () => {
        if (!selectedPerson?.id) return;
        setLoading(true);
        try {
            const [resSituation, resSettings] = await Promise.all([
                authFetch.get(`/situation/${selectedYear}?personId=${selectedPerson.id}`),
                authFetch.get(`/api/budget-settings/${selectedPerson.id}/${selectedYear}`)
            ]);

            const data = await resSituation.json();
            const settings = await resSettings.json();
            setRawRows(data);

            if (settings) {
                setMode(settings.preferred_mode);
                const sData = {
                    epargne: parseFloat(settings.simu_epargne_souhaitee),
                    salaire: parseFloat(settings.simu_salaire_annuel),
                    depenses: parseFloat(settings.simu_depenses_annuelles),
                    mois: parseInt(settings.simu_nb_mois),
                    viewType: settings.simu_view_type || 'annual'
                };
                setSimuData(sData);
                // Initialiser les inputs texte
                setTempInputs({
                    salaire: formatInputVal(sData.viewType === 'monthly' ? sData.salaire / sData.mois : sData.salaire),
                    depenses: formatInputVal(sData.viewType === 'monthly' ? sData.depenses / sData.mois : sData.depenses),
                    epargne: formatInputVal(sData.epargne)
                });
            } else {
                const totalIncomes = data.filter(r => r.category_flow === 'R').reduce((acc, r) => acc + parseFloat(r.real_n || 0), 0);
                const totalExpenses = data.filter(r => r.category_flow === 'D').reduce((acc, r) => acc + parseFloat(r.real_n || 0), 0);
                setMode('beginner');
                const defaultData = { epargne: 350, salaire: totalIncomes, depenses: totalExpenses, mois: 12, viewType: 'annual' };
                setSimuData(defaultData);
                setTempInputs({
                    salaire: formatInputVal(defaultData.salaire),
                    depenses: formatInputVal(defaultData.depenses),
                    epargne: formatInputVal(defaultData.epargne)
                });
            }
        } catch (err) { console.error(err); } finally { setLoading(false); }
    }, [selectedYear, selectedPerson?.id]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // --- GESTIONNAIRES ---

    const handleModeChange = (newMode) => {
        setMode(newMode);
        saveSettings({ mode: newMode });
    };

    const handleSimuValueChange = (field, stringValue) => {
        let numericVal = parseLocaleNumber(stringValue);
        
        // 💡 Appliquer la multiplication par 12 si on saisit en mode mensuel
        if (simuData.viewType === 'monthly') {
            numericVal = numericVal * 12;
        }

        const newSimu = { ...simuData, [field]: numericVal };
        setSimuData(newSimu);
        saveSettings(newSimu);
    };

    const resetSimulation = () => {
        const realData = {
            ...simuData,
            salaire: totalSalaires, // Vos variables issues du calcul expert/réel
            depenses: totalFactures,
            // On peut soit garder l'épargne souhaitée actuelle, soit la remettre à 0
        };
        setSimuData(realData);
        saveSettings(realData);
        setTempInputs({
            salaire: formatInputVal(simuData.viewType === 'monthly' ? totalSalaires / 12 : totalSalaires),
            depenses: formatInputVal(simuData.viewType === 'monthly' ? totalFactures / 12 : totalFactures),
            epargne: formatInputVal(simuData.viewType === 'monthly' ? simuData.epargne / 12 : simuData.epargne)
        });
    };

    useEffect(() => {
        if (simuData) {
            setTempInputs({
                salaire: formatInputVal(simuData.viewType === 'monthly' ? simuData.salaire / 12 : simuData.salaire),
                depenses: formatInputVal(simuData.viewType === 'monthly' ? simuData.depenses / 12 : simuData.depenses),
                epargne: formatInputVal(simuData.viewType === 'monthly' ? simuData.epargne / 12 : simuData.epargne)
            });
        }
    }, [simuData]);

    // --- LOGIQUE CALCULS ---

    const structuredData = useMemo(() => {
        const categories = {};
        if (!Array.isArray(rawRows)) return [];
        rawRows.forEach(row => {
            const key = `${row.category_id}_${row.category_flow}`;
            if (!categories[key]) {
                categories[key] = { id: row.category_id, name: row.category_name, flow: row.category_flow, totalReal: 0, totalBudget: 0, months: {} };
            }
            const real = parseFloat(row.real_n) || 0;
            const budget = parseFloat(row.budget_amount) || 0;
            categories[key].totalReal += real;
            categories[key].totalBudget += budget;
            categories[key].months[row.month] = { real_n: real, budget_amount: budget };
        });
        return Object.values(categories);
    }, [rawRows]);

    const expenses = structuredData.filter(c => c.flow === 'D');
    const incomes = structuredData.filter(c => c.flow === 'R');

    const salaireAnnuelSimu = simuData.salaire;
    const depensesAnnuellesSimu = simuData.depenses;
    const epargneAnnuelleSouhaitee = simuData.epargne;
    const epargneSimuleeTotale = salaireAnnuelSimu - depensesAnnuellesSimu;
    const economiesAFaireTotale = epargneAnnuelleSouhaitee - epargneSimuleeTotale;
    const economiesAFaireMensuelle = economiesAFaireTotale / 12;

    const chartData = useMemo(() => {
        return [...expenses]
            .map(c => ({ name: c.name, value: c.totalReal }))
            .sort((a, b) => a.value - b.value);
    }, [expenses]);

    const beginnerStats = useMemo(() => {
        if (!Array.isArray(rawRows) || rawRows.length === 0) return { totalR: 0, totalD: 0, nbMois: 0 };
        const activeMonths = rawRows.filter(r => parseFloat(r.real_n) > 0).map(r => parseInt(r.month));
        if (activeMonths.length === 0) return { totalR: 0, totalD: 0, nbMois: 0 };
        const firstMonth = Math.min(...activeMonths);
        let lastMonth = (parseInt(selectedYear) < currentYear) ? 12 : (parseInt(selectedYear) > currentYear ? 0 : currentMonthIndex);
        const nbMois = lastMonth >= firstMonth ? (lastMonth - firstMonth + 1) : 0;
        const filteredRows = rawRows.filter(r => { const m = parseInt(r.month); return m >= firstMonth && m <= lastMonth; });
        const totalR = filteredRows.filter(r => r.category_flow === 'R').reduce((acc, r) => acc + parseFloat(r.real_n || 0), 0);
        const totalD = filteredRows.filter(r => r.category_flow === 'D').reduce((acc, r) => acc + parseFloat(r.real_n || 0), 0);
        return { totalR, totalD, nbMois, firstMonth, lastMonth };
    }, [rawRows, selectedYear, currentYear, currentMonthIndex]);

    const { totalR, totalD, nbMois } = beginnerStats;
    const epargneActuelle = totalR - totalD;
    const epargneParMois = nbMois > 0 ? (epargneActuelle / nbMois) : 0;
    
    const totalSalaires = nbMois > 0 ? (totalR / nbMois) * 12 : 0;
    const totalFactures = nbMois > 0 ? (totalD / nbMois) * 12 : 0;

    // --- FONCTIONS EXPERT ---
    const isFuture = (monthIdx) => (selectedYear > currentYear) || (selectedYear === currentYear && monthIdx > currentMonthIndex);
    const getMonthTotal = (cats, monthIdx, field) => cats.reduce((sum, c) => sum + (c.months[monthIdx + 1]?.[field] || 0), 0);
    const getRecettesDisplayTotal = (monthIdx) => incomes.reduce((sum, cat) => {
        const m = cat.months[monthIdx + 1] || { real_n: 0, budget_amount: 0 };
        return sum + (m.real_n !== 0 ? m.real_n : m.budget_amount);
    }, 0);
    const getEpargneDisplay = (monthIdx) => {
        const recettes = getRecettesDisplayTotal(monthIdx);
        const reelD = getMonthTotal(expenses, monthIdx, 'real_n');
        const budgetD = getMonthTotal(expenses, monthIdx, 'budget_amount');
        const depPourCalcul = (reelD !== 0 && !isFuture(monthIdx)) ? reelD : budgetD;
        return recettes - depPourCalcul;
    };
    const getCumulativeEcart = (cat) => {
        let limit = (selectedYear < currentYear) ? 12 : (selectedYear > currentYear ? 0 : currentMonthIndex + 1);
        const catsArr = Array.isArray(cat) ? cat : [cat];
        let total = 0;
        catsArr.forEach(c => { for (let m = 1; m <= limit; m++) { const d = c.months[m] || { budget_amount: 0, real_n: 0 }; total += (d.budget_amount - d.real_n); } });
        return total;
    };

    const getColor = (index, total) => {
        const ratio = index / (total - 1);
        const r = Math.floor(255 * ratio);
        const g = Math.floor(255 * (1 - ratio));
        return `rgb(${r},${g},0)`;
    };

    if (!selectedPerson?.id) return <div style={{ padding: '20px', textAlign: 'center' }}>Veuillez sélectionner une personne.</div>;
    if (loading || mode === null) return <div style={{ padding: '20px' }}>Chargement de la situation...</div>;

    return (
        <div style={styles.container}>
            <div style={styles.headerActions}>
                <h2 style={{ color: themeColors.secondary, margin: 0 }}>Situation Budgétaire - {selectedPerson.name}</h2>
                <div style={styles.modeToggle}>
                    <button style={{ ...styles.toggleBtn, backgroundColor: mode === 'beginner' ? themeColors.primary : '#eee', color: mode === 'beginner' ? '#fff' : '#333' }} onClick={() => handleModeChange('beginner')}>Débutant</button>
                    <button style={{ ...styles.toggleBtn, backgroundColor: mode === 'expert' ? themeColors.primary : '#eee', color: mode === 'expert' ? '#fff' : '#333' }} onClick={() => handleModeChange('expert')}>Expert</button>
                </div>
            </div>

            {mode === 'beginner' ? (
                <div id="beginner-mode">
                    <div style={styles.topGrid}>
                        <div style={styles.card}>
                            <h3 style={{ color: themeColors.primary, marginTop: 0 }}>Situation Actuelle (Mois révolus)</h3>
                            {nbMois > 0 ? (
                                <>
                                    <div style={styles.dataLine}><span>Total Recettes :</span><strong>{totalR.toLocaleString()} €</strong></div>
                                    <div style={styles.dataLine}><span>Total Factures :</span><strong>{totalD.toLocaleString()} €</strong></div>
                                    <div style={styles.dataLine}><span>Période :</span><span style={{ fontSize: '0.85em', fontStyle: 'italic' }}>({nbMois} mois : de {MONTHS[beginnerStats.firstMonth - 1]} à {MONTHS[beginnerStats.lastMonth - 1]})</span></div>
                                    <div style={styles.dataLine}><span>Épargne cumulée :</span><strong style={{color: epargneActuelle >= 0 ? 'green':'red'}}>{epargneActuelle.toLocaleString()} €</strong></div>
                                    <div style={{...styles.dataLine, borderBottom: 'none', backgroundColor: '#f9f9f9', padding: '10px 5px', marginTop: '10px'}}>
                                        <span>Épargne moyenne / mois :</span>
                                        <strong style={{fontSize: '1.1em', color: epargneActuelle >= 0 ? 'green':'red'}}>{epargneParMois.toLocaleString(undefined, {maximumFractionDigits: 2})} €</strong>
                                    </div>
                                </>
                            ) : <p>Données insuffisantes.</p>}
                        </div>

                        <div style={styles.card}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <h3 style={{ color: themeColors.secondary, margin: 0 }}>Simulation</h3>
                                <div style={styles.modeToggle}>
                                    <button 
                                        style={{ ...styles.toggleBtn, padding: '4px 10px', fontSize: '0.75em', backgroundColor: simuData.viewType === 'monthly' ? themeColors.secondary : '#eee', color: simuData.viewType === 'monthly' ? '#fff' : '#333' }}
                                        onClick={() => { const n = { ...simuData, viewType: 'monthly' }; setSimuData(n); saveSettings(n); }}
                                    >Mensuel</button>
                                    <button 
                                        style={{ ...styles.toggleBtn, padding: '4px 10px', fontSize: '0.75em', backgroundColor: simuData.viewType === 'annual' ? themeColors.secondary : '#eee', color: simuData.viewType === 'annual' ? '#fff' : '#333' }}
                                        onClick={() => { const n = { ...simuData, viewType: 'annual' }; setSimuData(n); saveSettings(n); }}
                                    >Annuel</button>
                                </div>
                            </div>

                            {/* Inputs de saisie */}
                            <div style={styles.dataLine}>
                                <span>Salaire {simuData.viewType === 'monthly' ? 'mensuel' : 'annuel'} prévu :</span>
                                <input 
                                    type="text" style={styles.input} value={tempInputs.salaire} 
                                    onFocus={(e) => e.target.select()}
                                    onChange={e => setTempInputs(prev => ({...prev, salaire: e.target.value}))}
                                    onBlur={() => handleSimuValueChange('salaire', tempInputs.salaire)}
                                />
                            </div>
                            
                            <div style={styles.dataLine}>
                                <span>Dépenses {simuData.viewType === 'monthly' ? 'mensuelles' : 'annuelles'} prévues :</span>
                                <input 
                                    type="text" style={styles.input} value={tempInputs.depenses} 
                                    onFocus={(e) => e.target.select()}
                                    onChange={e => setTempInputs(prev => ({...prev, depenses: e.target.value}))}
                                    onBlur={() => handleSimuValueChange('depenses', tempInputs.depenses)}
                                />
                            </div>

                            <div style={styles.dataLine}>
                                <span>Épargne {simuData.viewType === 'monthly' ? 'mensuelle' : 'annuelle'} souhaitée :</span>
                                <input 
                                    type="text" style={styles.input} value={tempInputs.epargne}
                                    onFocus={(e) => e.target.select()}
                                    onChange={e => setTempInputs(prev => ({...prev, epargne: e.target.value}))}
                                    onBlur={() => handleSimuValueChange('epargne', tempInputs.epargne)}
                                />
                            </div>

                            {/* Résultat - Reprend toute sa place et sa visibilité */}
                            <div style={{ ...styles.dataLine, marginTop: '20px', borderTop: '2px solid #eee', paddingTop: '15px' }}>
                                <span style={{ fontWeight: 'bold' }}>Économies à faire ({simuData.viewType === 'monthly' ? 'par mois' : 'sur un an'}) :</span>
                                <strong style={{ color: economiesAFaireTotale > 0 ? 'red' : 'green', fontSize: '1.2em' }}>
                                    {simuData.viewType === 'monthly' 
                                        ? formatCurrency(Math.abs(economiesAFaireMensuelle)) 
                                        : formatCurrency(Math.abs(economiesAFaireTotale))
                                    } 
                                    {economiesAFaireTotale > 0 ? ' manquants' : ' en surplus'}
                                </strong>
                            </div>

                            {/* Bouton Réinitialiser - Placé en dehors du flux de données, tout en bas */}
                            <div style={{ textAlign: 'right', marginTop: '15px' }}>
                                <button 
                                    onClick={resetSimulation}
                                    style={{ 
                                        border: 'none', 
                                        background: 'none', 
                                        color: '#90a4ae', 
                                        textDecoration: 'underline', 
                                        cursor: 'pointer', 
                                        fontSize: '0.75em',
                                        transition: '0.3s'
                                    }}
                                    onMouseOver={(e) => e.target.style.color = themeColors.secondary}
                                    onMouseOut={(e) => e.target.style.color = '#90a4ae'}
                                >
                                    Réinitialiser avec les valeurs réelles projetées
                                </button>
                            </div>
                        </div>
                    </div>
                    <div style={styles.chartContainer}>
                        <h3 style={{ textAlign: 'center', marginBottom: '20px', fontSize: '1em' }}>Répartition annuelle des dépenses par catégorie</h3>
                        <ResponsiveContainer width="100%" height="90%">
                            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 30, bottom: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} height={80} style={{fontSize: '10px'}} />
                                <YAxis tickFormatter={(v) => `${v}€`} style={{fontSize: '10px'}} />
                                <Tooltip formatter={(v) => [`${v.toLocaleString()} €`, 'Total annuel']} />
                                <Bar dataKey="value">
                                    {chartData.map((entry, index) => <Cell key={`cell-${index}`} fill={getColor(index, chartData.length)} />)}
                                    <LabelList dataKey="value" position="top" formatter={(v) => `${v.toLocaleString()}€`} style={{fontSize: '9px'}}/>
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            ) : (
                /* --- MODE EXPERT --- */
                <div style={{ width: '100%', overflowX: 'auto' }}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={{...styles.th, width: '150px'}}>Catégories</th>
                                {MONTHS.map((m, i) => <th key={m} style={{ ...styles.th, opacity: isFuture(i) ? 0.6 : 1 }}>{m}</th>)}
                                <th style={{...styles.th, width: '80px'}}>Cumul</th>
                                <th style={{ ...styles.th, backgroundColor: '#e1f5fe', width: '80px' }}>MENSUEL</th>
                                <th style={{ ...styles.th, backgroundColor: '#e8f5e9', width: '80px' }}>ANNUEL</th>
                            </tr>
                        </thead>
                        <tbody>
                            {expenses.map(cat => {
                                const annualTotal = MONTHS.reduce((s, _, i) => s + (cat.months[i+1]?.budget_amount || 0), 0);
                                const ecartCumule = getCumulativeEcart(cat);
                                const cumulStyle = getBudgetContextualStyle(ecartCumule, false, false, themeColors);
                                return (
                                    <tr key={cat.id}>
                                        <td style={styles.categoryCell}>{cat.name}</td>
                                        {MONTHS.map((_, i) => (
                                            <SituationCell key={i} person_id={selectedPerson.id} catId={cat.id} month={i+1} year={selectedYear} data={cat.months[i+1]} themeColors={themeColors} onUpdate={fetchData} isFuture={isFuture(i)} />
                                        ))}
                                        <td style={{ ...styles.summaryValue, ...cumulStyle, fontWeight: 'bold' }}>{formatCurrency(ecartCumule)}</td>
                                        <MonthlyBudgetCell person_id={selectedPerson.id} catId={cat.id} year={selectedYear} currentMonthly={annualTotal / 12} onUpdate={fetchData} />
                                        <AnnualBudgetCell person_id={selectedPerson.id} catId={cat.id} year={selectedYear} currentTotal={annualTotal} onUpdate={fetchData} />
                                    </tr>
                                );
                            })}
                            <tr style={{ height: '20px' }}><td colSpan={15}></td></tr>
                            <tr style={styles.summaryRow}>
                                <td style={styles.summaryLabel}>TOTAL BUDGET (Dépenses)</td>
                                {MONTHS.map((_, i) => <td key={i} style={{ ...styles.summaryValue, ...(isFuture(i) && styles.futureMonth) }}>{formatCurrency(getMonthTotal(expenses, i, 'budget_amount'))}</td>)}
                                <td style={{ backgroundColor: '#f2f2f2', border: '1px solid #ccc' }}></td>
                                <td colSpan="2" style={styles.summaryValue}>{formatCurrency(expenses.reduce((acc, cat) => acc + MONTHS.reduce((s, _, i) => s + (cat.months[i+1]?.budget_amount || 0), 0), 0))}</td>
                            </tr>
                            <tr style={styles.summaryRow}>
                                <td style={styles.summaryLabel}>TOTAL RÉEL (Dépenses)</td>
                                {MONTHS.map((_, i) => <td key={i} style={{ ...styles.summaryValue, ...(isFuture(i) && styles.futureMonth) }}>{formatCurrency(getMonthTotal(expenses, i, 'real_n'))}</td>)}
                                <td style={{ backgroundColor: '#f2f2f2', border: '1px solid #ccc' }}></td>
                                <td colSpan="2" style={styles.summaryValue}>{formatCurrency(expenses.reduce((acc, cat) => acc + MONTHS.reduce((s, _, i) => s + (cat.months[i+1]?.real_n || 0), 0), 0))}</td>
                            </tr>
                            <tr style={styles.summaryRow}>
                                <td style={styles.summaryLabel}>ECART (B - R)</td>
                                {MONTHS.map((_, i) => {
                                    const ecart = getMonthTotal(expenses, i, 'budget_amount') - getMonthTotal(expenses, i, 'real_n');
                                    return <td key={i} style={{ ...styles.summaryValue, ...(isFuture(i) ? styles.futureMonth : { color: ecart < 0 ? 'red' : 'green' }) }}>{formatCurrency(ecart)}</td>;
                                })}
                                {(() => {
                                    const totalEcart = getCumulativeEcart(expenses);
                                    const totalStyle = getBudgetContextualStyle(totalEcart, false, false, themeColors);
                                    return <td style={{ ...styles.summaryValue, ...totalStyle, fontWeight: 'bold' }}>{formatCurrency(totalEcart)}</td>;
                                })()}
                                <td colSpan="2" style={styles.summaryValue}>{formatCurrency(expenses.reduce((acc, cat) => acc + MONTHS.reduce((s, _, i) => s + (cat.months[i+1]?.budget_amount || 0), 0), 0) - expenses.reduce((acc, cat) => acc + MONTHS.reduce((s, _, i) => s + (cat.months[i+1]?.real_n || 0), 0), 0))}</td>
                            </tr>
                            <tr style={{ height: '20px' }}><td colSpan={15}></td></tr>
                            {incomes.map(cat => {
                                const annualBudget = MONTHS.reduce((s, _, i) => s + (cat.months[i+1]?.budget_amount || 0), 0);
                                return (
                                    <tr key={cat.id}>
                                        <td style={styles.categoryCell}>{cat.name}</td>
                                        {MONTHS.map((_, i) => <SituationCell key={i} person_id={selectedPerson.id} catId={cat.id} month={i+1} year={selectedYear} data={cat.months[i+1]} themeColors={themeColors} onUpdate={fetchData} isIncome={true} isFuture={isFuture(i)} />)}
                                        <td style={{ backgroundColor: '#f2f2f2', border: '1px solid #ccc' }}></td>
                                        <MonthlyBudgetCell person_id={selectedPerson.id} catId={cat.id} year={selectedYear} currentMonthly={annualBudget / 12} onUpdate={fetchData} />
                                        <AnnualBudgetCell person_id={selectedPerson.id} catId={cat.id} year={selectedYear} currentTotal={annualBudget} onUpdate={fetchData} />
                                    </tr>
                                );
                            })}
                            <tr style={{ ...styles.summaryRow, color: themeColors.success }}>
                                <td style={styles.summaryLabel}>RECETTES (Réel ou Budget)</td>
                                {MONTHS.map((_, i) => <td key={i} style={{ ...styles.summaryValue, ...(isFuture(i) && styles.futureMonth) }}>{formatCurrency(getRecettesDisplayTotal(i))}</td>)}
                                <td style={{ backgroundColor: '#f2f2f2', border: '1px solid #ccc' }}></td>
                                <td colSpan="2" style={styles.summaryValue}>{formatCurrency(MONTHS.reduce((acc, _, i) => acc + getRecettesDisplayTotal(i), 0))}</td>
                            </tr>
                            <tr style={{ height: '20px' }}><td colSpan={15}></td></tr>
                            <tr style={{ ...styles.summaryRow, backgroundColor: themeColors.success + '20' }}>
                                <td style={styles.summaryLabel}>EPARGNE (Recettes - Dépenses)</td>
                                {MONTHS.map((_, i) => <td key={i} style={{ ...styles.summaryValue, ...(isFuture(i) && styles.futureMonth) }}><strong>{formatCurrency(getEpargneDisplay(i))}</strong></td>)}
                                <td style={{ backgroundColor: '#f2f2f2', border: '1px solid #ccc' }}></td>
                                <td colSpan="2" style={styles.summaryValue}>{formatCurrency(MONTHS.reduce((acc, _, i) => acc + getEpargneDisplay(i), 0))}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}